import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import DynamicLink from '@atoms/DynamicLink/DynamicLink'
import {
  searchUrlBuilder,
  buildSuggestionUrl,
  useEinstein,
  useCommerceSFRAAPI,
  useNavigation,
  useMultiSite,
  useConfig
} from 'msp-integrations'
import Icons from '@atoms/Icons/Icons'
import IconButton from '@molecules/IconButton/IconButton'
import Field from '@molecules/Field/Field'
import getSearchBoxFields from './SearchBox.fields'
import { SearchBoxProps, SearchBoxResultsProps } from './SearchBox.types'

// List of results type to be considered ordered as they should be displayed
const seachBoxSortedResultType = [
  'productSuggestions',
  'categorySuggestions',
  'contentSuggestions'
]

/**
 * SearchBox component to query custom SFRA search endpoint
 */
const SearchBox = ({
  className,
  classNameResults = '',
  formRef,
  inputIcon,
  inputPlaceholder,
  inputClassName = '',
  position = 'absolute',
  onResultClick,
  onSearching,
  onSearch,
  onClose,
  showDeleteButton,
  searchValue,
  iconColor,
  uiConfig,
  isDarkMode = false
}: SearchBoxProps) => {
  const { formatMessage } = useIntl()
  const {
    theme: {
      font: { regular: font, wide: fontWide }
    }
  } = useConfig()
  const SFRAClient = useCommerceSFRAAPI()
  const navigate = useNavigation()
  const { buildUrl, locale } = useMultiSite()
  const [searchTerm, setSearchTerm] = useState(searchValue || '')
  const [rawResults, setRawResults] =
    useState<SearchBoxResultsProps['rawResults']>()
  const [results, setResults] = useState<Record<string, []>>({})
  const [hasResults, setHasResults] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [hideResults, setHideResults] = useState<boolean>(true)
  const [isFocused, setIsFocused] = useState(false)
  const [timer, setTimer] = useState<number>()
  const form = useForm()

  const fieldConfig = getSearchBoxFields(
    inputPlaceholder,
    (e: any) => {
      setSearchTerm(e.target.value)
    },
    () => {
      setIsFocused(false)
    }
  )

  const closeAll = () => {
    onClose && onClose()
    onResultClick && onResultClick()
  }

  useEffect(() => {
    if (searchValue) {
      setSearchTerm(searchValue)
      setIsFocused(false)
    }
  }, [searchValue])

  useEffect(() => {
    form.setValue('searchInput', searchTerm)
  }, [searchTerm])

  const handleResponse = (
    error: any,
    res?: SearchBoxResultsProps['rawResults']
  ) => {
    let resultsToSet: Record<string, []> = {}
    if (res) {
      resultsToSet = res.results

      resultsToSet.productSuggestions = (
        resultsToSet.productSuggestions || []
      ).filter(
        ({ c_showSuggestionHeadless }: Record<string, boolean | null>) =>
          String(c_showSuggestionHeadless) !== 'false'
      ) as []

      setRawResults(res)
    }
    onSearch ? onSearch(resultsToSet, searchTerm) : setResults(resultsToSet)
    onSearching ? onSearching(false) : setIsSearching(false)
  }

  useEffect(() => {
    return () => {
      clearTimeout(timer)
    }
  }, [])

  const searchResults = (searchTerm: string, locale: string) => {
    clearTimeout(timer)
    onSearching ? onSearching(true) : setIsSearching(true)
    setHideResults(false)
    setTimer(
      window.setTimeout(() => {
        SFRAClient.OcapiSearch.searchSuggestions({
          query: { q: searchTerm, locale: locale }
        })
          .then((res: any) => {
            handleResponse(res.error, res)
          })
          .catch((error: any) => {
            handleResponse(error)
          })
      }, 600)
    )
  }

  const redirectToSearch = (queryParam: string) => {
    if (queryParam.length >= 3) {
      clearTimeout(timer)
      closeAll()
      setHideResults(true)
      const path = buildUrl(searchUrlBuilder(queryParam))
      navigate(path)
    }
  }

  const clearSearch = () => {
    !searchTerm.length && closeAll()
    clearTimeout(timer)
    onSearching ? onSearching(false) : setIsSearching(false)
    onSearch ? onSearch({}, searchTerm) : setResults({})
    setSearchTerm('')
  }

  useMemo(() => {
    const totalResults = seachBoxSortedResultType.reduce(
      (acc: any, curr: any) => {
        return acc + (results[curr] || []).length
      },
      0
    )
    setHasResults(totalResults !== 0)
  }, [results])

  useEffect(() => {
    isFocused &&
      searchTerm.length &&
      searchTerm.length > 2 &&
      searchResults(searchTerm.trim(), locale.id)

    isFocused && setHideResults(searchTerm.length < 3)
  }, [searchTerm])

  const handleFocus = () => {
    setSearchTerm('')
    setIsFocused(true)
  }

  const handleKeyDown = (e) => {
    e.stopPropagation()
    e.keyCode === 27 && clearSearch()
    e.keyCode === 13 && redirectToSearch(e.target.value)
  }

  return (
    <div className={twMerge('searchBoxGroup relative', font, className)}>
      <div className='searchBoxInput relative'>
        <form
          ref={formRef}
          onSubmit={form.handleSubmit(() => {
            redirectToSearch(searchTerm)
          })}
        >
          <Field
            control={form.control}
            config={fieldConfig.searchInput}
            error={form.errors?.searchInput}
            className={isDarkMode ? 'mt-0' : 'mt-[-0.75rem]'}
            additionalProps={{
              layout: 'underline',
              inputClassName: twMerge(
                'tracking-0 text-16 md:text-24 md:leading-28 h-12 w-full pr-10 md:h-14' +
                  inputClassName
              ),
              onFocus: handleFocus,
              onKeyDown: handleKeyDown,
              isDarkMode: isDarkMode
            }}
          />

          <div
            style={{ width: '20px' }}
            className='absolute bottom-3.5 right-0 text-center md:bottom-4'
          >
            {inputIcon || (searchTerm.length && showDeleteButton) ? (
              <IconButton
                iconName='Close'
                height='20px'
                width='20px'
                color={iconColor || 'machine2-50'}
                onClick={clearSearch}
              />
            ) : (
              <IconButton
                iconName={uiConfig?.searchIconName ?? 'Search'}
                width='20px'
                height='20px'
                color={iconColor || 'machine2-50'}
                onClick={() => redirectToSearch(searchTerm)}
              />
            )}
          </div>
        </form>
      </div>

      {!hideResults && !onSearch && searchTerm.length > 0 && (
        <ul
          className={twMerge(
            'text-16 border-lightGray z-10 w-full bg-white px-4 py-5 shadow-lg md:min-h-full',
            position,
            classNameResults
          )}
        >
          {isSearching && (
            <li className='py-2'>
              {formatMessage({
                defaultMessage: 'Searching...',
                id: 'searchBox.searching'
              })}
            </li>
          )}
          {!isSearching && !hasResults && (
            <li className='py-2'>
              {formatMessage({
                defaultMessage: 'No results found',
                id: 'searchBox.noResults'
              })}
            </li>
          )}
          {!isSearching && hasResults && (
            <>
              <SearchBoxResults
                rawResults={rawResults}
                results={results}
                onResultClick={onResultClick}
                buildUrl={buildUrl}
                searchQuery={searchTerm}
                uiConfig={uiConfig}
              />
              <li className='mt-8'>
                <p
                  className={twMerge(
                    'text-10 inline-block cursor-pointer rounded-none border-0 border-b border-black pb-0.5 uppercase',
                    fontWide,
                    uiConfig?.resultTitleLetterSpacing
                  )}
                  onClick={() => redirectToSearch(searchTerm)}
                >
                  {formatMessage({
                    defaultMessage: 'Redirect to search results',
                    id: 'searchBox.redirectToSearch'
                  })}
                </p>
              </li>
            </>
          )}
        </ul>
      )}
    </div>
  )
}

/**
 * SearchBoxResults component to display search results
 **/
const SearchBoxResults = ({
  rawResults,
  results,
  onResultClick,
  searchQuery,
  buildUrl,
  uiConfig
}: SearchBoxResultsProps) => {
  const { formatMessage } = useIntl()
  const einstein = useEinstein()
  const {
    theme: {
      font: { regular: font, wide: fontWide }
    }
  } = useConfig()

  const resultTypeNames = {
    productSuggestions: formatMessage({
      id: 'searchBox.searchType.productSuggestions',
      defaultMessage: 'Products'
    }),
    categorySuggestions: formatMessage({
      id: 'searchBox.searchType.categorySuggestions',
      defaultMessage: 'Categories'
    }),
    contentSuggestions: formatMessage({
      id: 'searchBox.searchType.contentSuggestions',
      defaultMessage: 'Content'
    })
  }

  return (
    <>
      {seachBoxSortedResultType
        .filter((ResultType) => {
          return (results[ResultType] || []).length > 0
        })
        .map((resultType) => (
          <li
            key={resultType}
            className={twMerge(
              'my-4 border-t border-black first:border-none',
              uiConfig?.sugggestionTypeStyle
            )}
          >
            <p
              className={twMerge(
                'text-10 mb-4 mt-6 uppercase',
                fontWide,
                uiConfig?.resultLetterSpacing
              )}
            >
              {resultTypeNames[resultType as keyof typeof resultTypeNames]}
            </p>
            <ul>
              {results[resultType]
                .slice(0, rawResults?.maxSuggestions || 3)
                .map((item: Record<string, any>) => (
                  <li
                    key={item.url}
                    value={item.name}
                    onClick={() => onResultClick && onResultClick()}
                  >
                    <DynamicLink
                      href={
                        buildUrl(item.headlessUrl) ||
                        buildSuggestionUrl(resultType, item, buildUrl)
                      }
                      onClick={() => {
                        if (resultType === seachBoxSortedResultType[0]) {
                          einstein.sendClickSearch(searchQuery, {
                            productId: item.id
                          })
                        }
                      }}
                    >
                      <div
                        className={twMerge(
                          'hover:bg-machine1-50 flex items-center justify-between',
                          uiConfig?.suggestionLineStyle
                        )}
                      >
                        <div className='flex w-full items-center justify-start py-2'>
                          <div className='mr-2 inline-block'>
                            {item.imageUrl &&
                              resultType !== seachBoxSortedResultType[1] && (
                                <img
                                  src={item.imageUrl}
                                  alt={item.name}
                                  width='50px'
                                />
                              )}
                          </div>
                          <div>
                            <p
                              className={`${
                                item.imageUrl &&
                                resultType !== seachBoxSortedResultType[1]
                                  ? fontWide
                                  : font
                              } text-16 tracking-0`}
                            >
                              {item.name}
                            </p>
                            {item.category && (
                              <p className={`${font} text-grayRegular text-14`}>
                                {item.category}
                              </p>
                            )}
                          </div>
                        </div>
                        {(!item.imageUrl ||
                          resultType === seachBoxSortedResultType[1]) && (
                          <Icons
                            color='black'
                            iconName='ArrowRight'
                            size='2xl'
                          />
                        )}
                      </div>
                    </DynamicLink>
                  </li>
                ))}
            </ul>
          </li>
        ))}
    </>
  )
}

export default SearchBox
